import * as Sentry from '@sentry/react';
import { QRCodeSVG } from 'qrcode.react';
import { FormEvent, useEffect, useState } from 'react';

import type { TotpSetup } from '@/auth/utils/cognitoSignIn';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';
import { ERROR_CODES, UnauthorizedError } from '@/core/api';

interface CognitoTotpEnrolmentProps {
  /** Resolves with the secret to add to an authenticator app. */
  start: () => Promise<TotpSetup>;
  /** Verifies the first code; rejects with `auth.otp_invalid` for a wrong one. */
  verify: (code: string) => Promise<void>;
  onEnrolled: () => void;
  /** The sign-in session lapsed (`auth.token_expired`): the caller restarts sign-in. */
  onExpired: () => void;
  /** In a dialog (Settings): a Cancel beside Verify, in one right-aligned row. */
  onCancel?: () => void;
}

function isExpired(err: unknown): boolean {
  return err instanceof UnauthorizedError && err.code === ERROR_CODES.AUTH_TOKEN_EXPIRED;
}

function errorMessage(err: unknown): string {
  if (err instanceof UnauthorizedError) {
    return err.code === ERROR_CODES.AUTH_OTP_INVALID ? 'Invalid verification code' : err.message;
  }
  Sentry.captureException(err);
  return 'Sorry, an error occurred. Please try again.';
}

// Groups of four, the way authenticator apps print setup keys.
function formatKey(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

/**
 * TOTP enrolment, during sign-in (ENG-6103) and from the security settings
 * (ENG-6107): the user scans the QR code or types the key into an
 * authenticator app and confirms with the first code.
 */
export function CognitoTotpEnrolment({
  start,
  verify,
  onEnrolled,
  onExpired,
  onCancel,
}: CognitoTotpEnrolmentProps): JSX.Element {
  const [setup, setSetup] = useState<TotpSetup>();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Bumped by "Try again" to rerun start() after a failure.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    // A new start never shows the previous secret, even for a moment.
    setSetup(undefined);
    start()
      .then((result) => live && setSetup(result))
      .catch((err: unknown) => {
        if (!live) return;
        if (isExpired(err)) onExpired();
        else setError(errorMessage(err));
      });
    return () => {
      live = false;
    };
  }, [start, onExpired, attempt]);

  function handleRetry() {
    setError(undefined);
    setAttempt((n) => n + 1);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await verify(code);
      onEnrolled();
    } catch (err) {
      if (isExpired(err)) {
        onExpired();
        return;
      }
      setError(errorMessage(err));
      setCode('');
      setBusy(false);
    }
  }

  return (
    <form className="mx-auto mt-24 flex max-w-[420px] flex-col gap-16" onSubmit={handleSubmit}>
      <p className="text-on-surface">
        Scan the QR code with an authentication app such as Google Authenticator, then enter the
        6-digit code it shows.
      </p>
      {setup && (
        // The QR code, the link and the key carry the TOTP seed: keep them out
        // of Sentry Replay entirely (masking hides text, not the href).
        <div className="sentry-block flex flex-col gap-8" data-sentry-block>
          <QRCodeSVG
            className="mx-auto"
            value={setup.setupUri}
            size={176}
            marginSize={2}
            title="Two-factor authentication QR code"
          />
          <a className="text-brand-primary underline" href={setup.setupUri}>
            Open in your authentication app
          </a>
          <span className="text-on-surface">Or enter this setup key:</span>
          <code className="break-all rounded-md bg-surface-secondary p-8 text-14 text-on-surface">
            {formatKey(setup.sharedSecret)}
          </code>
        </div>
      )}
      <Input
        label="Verification Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        error={!!error}
        helperText={error}
        inputProps={{ autoComplete: 'one-time-code', inputMode: 'numeric' }}
      />
      {!setup && error && (
        <Button
          className="min-h-48 min-w-48"
          type="button"
          variant="secondary"
          onClick={handleRetry}
        >
          Try again
        </Button>
      )}
      {onCancel ? (
        <div className="flex justify-end gap-8">
          <Button type="button" variant="tertiary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={!setup || !code || busy}>
            Verify
          </Button>
        </div>
      ) : (
        <Button className="min-h-48 min-w-48" type="submit" disabled={!setup || !code || busy}>
          Verify
        </Button>
      )}
    </form>
  );
}
